import styled, { css } from 'styled-components';
import { animated } from '@react-spring/web';

import Link from 'Elements/Link';

export const Container = styled.article`
    ${({ theme }) => css`
        display: flex;
        flex-direction: column;

        @media ${theme.breakpoints.medium.media} {
            flex-direction: row;
        }
    `}
`;

export const ImageContainer = styled.div`
    ${({ theme }) => css`
        display: grid;
        place-items: center;
        position: relative;
        padding: 0 0 1.25rem;

        &:before {
            content: '';
            background: ${theme.colors.primaryContrast.hex};
            border-radius: 0.5rem 0.5rem 0 0;
            width: 100%;
            height: 3rem;
            position: absolute;
            bottom: 0;
            left: 0;
        }

        @media ${theme.breakpoints.medium.media} {
            padding: 2rem 0;

            &:before {
                border-radius: 0.5rem 0 0 0.5rem;
                width: 30%;
                height: 100%;
                top: 0;
                right: 0;
                bottom: unset;
                left: unset;
            }
        }

        @media ${theme.breakpoints.large.media} {
            &:before {
                width: 60%;
            }
        }
    `}
`;

export const ImageLink = styled(Link)`
    ${({ theme }) => css`
        display: block;
        width: 80%;
        max-width: 18rem;
        height: 7rem;
        border-radius: 0.25rem;
        overflow: hidden;

        @media ${theme.breakpoints.medium.media} {
            width: 14rem;
            height: 9rem;
            max-width: unset;
        }

        @media ${theme.breakpoints.large.media} {
            width: 18.5rem;
            height: 11.75rem;
        }
    `}
`;

export const ImageWrapper = styled(animated.div)`
    height: 100%;
    width: 100%;
`;

export const Inner = styled.div`
    ${({ theme }) => css`
        position: relative;
        background-color: ${theme.colors.primaryContrast.hex};
        border-radius: 0 0 0.5rem 0.5rem;
        padding: 0 1rem 1.5rem;
        width: 100%;
        display: flex;
        flex-direction: column;

        @media ${theme.breakpoints.medium.media} {
            padding: 1.5rem;
            border-radius: 0 0.5rem 0.5rem 0;
        }

        @media ${theme.breakpoints.large.media} {
            padding: 2rem 2.5rem;
        }
    `}
`;

export const NameLink = styled(Link)`
    display: block;
    color: ${({ theme }) => theme.colors.text.hex};
    text-decoration: none;
`;

export const DegreeType = styled.p`
    ${({ theme }) => css`
        font-family: ${theme.fonts.secondary};
        font-size: 1.125rem;
        margin: 0;
        line-height: 1.2;

        @media ${theme.breakpoints.large.media} {
            font-size: 1.25rem;
        }

        &:empty:before {
            content: ' ';
            white-space: pre;
        }
    `}
`;

export const Name = styled.h2`
    ${({ theme }) => css`
        font-size: 1.25rem;
        font-weight: 700;
        margin: 0.25rem 0 0;
        line-height: 1.2;

        @media ${theme.breakpoints.large.media} {
            font-size: 1.5rem;
            padding-right: 8.5rem;
        }
    `}
`;

export const CategoriesList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 1rem 0 0;
    padding: 0;
    list-style: none;
`;

export const PillItem = styled.li`
    margin: 0;
    font-size: 1rem;
`;

export const VariantsHeading = styled.p`
    ${({ theme }) => css`
        font-size: 1rem;
        margin: 1rem 0 0;

        @media ${theme.breakpoints.large.media} {
            font-size: 1.125rem;
        }
    `}
`;

export const VariantList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

export const VariantItem = styled.li`
    margin: 0;
`;

export const VariantLink = styled(Link)`
    ${({ theme }) => css`
        font-size: 1rem;
        font-weight: 400;
        line-height: 1;
        display: block;

        @media ${theme.breakpoints.large.media} {
            font-size: 1.125rem;
        }
    `}
`;

export const CompareButton = styled.button<{ $checked: boolean }>`
    ${({ theme, $checked }) => css`
        position: absolute;
        display: flex;
        align-items: center;
        top: -0.75rem;
        right: 1rem;
        padding: 0.3rem 0.6rem;
        background: ${$checked
            ? `${theme.colors.accents[5].hex}`
            : `${theme.colors.secondary.tints[0].hex}`};
        border: 0.0625rem solid ${theme.colors.secondary.tints[1].hex};
        border-radius: 1.125rem;
        font-size: 0.875rem;
        font-weight: 400;
        color: ${$checked
            ? `${theme.colors.primaryContrast.hex}`
            : `${theme.colors.secondaryContrast.hex}`};

        :hover {
            background: ${$checked
                ? `${theme.colors.accents[5].hex}`
                : `${theme.colors.secondary.tints[2].hex}`};
            cursor: pointer;
        }

        @media ${theme.breakpoints.medium.media} {
            top: 1rem;
        }

        @media ${theme.breakpoints.large.media} {
            top: 1.5rem;
            font-size: 1rem;
            padding: 0.4rem 0.875rem;
        }
    `}
`;

export const Icon = styled.svg<{ $checked: boolean }>`
    ${({ theme, $checked }) => css`
        width: 0.875rem;
        height: 0.875rem;
        margin-left: 0.5625rem;
        stroke-width: 0.15rem;

        ${$checked &&
        css`
            background-color: ${theme.colors.primaryContrast.hex};
            color: ${theme.colors.accents[5].hex};
            border-radius: 50%;
            padding: 0.15rem;
        `}

        @media ${theme.breakpoints.large.media} {
            width: 1rem;
            height: 1rem;
        }
    `}
`;
